import { CustomResponse } from "../model/data/common";
import { Post, PostDetail } from "../model/domain/news";
import { ShareCenterOption } from "../model/domain/share-option";
import TaskBase from "./TaskBase";
import Urls from "./Urls";

export default class TaskNews extends TaskBase {

    public fetchNews(url: string) {
        return fetch(Urls.URL_GET_NEWS + url, this.getRequestInfo())
            .then(response => response.json())
            .then(data => {
                return data;
            })
            .catch(error => {
                console.error('Error fetching news:', error);
                throw error;
            });
    }

    public getNewsDetail(newsId: Post['id']): Promise<PostDetail> {
        return fetch(`${Urls.URL_GET_NEWS}/${newsId}`, this.getRequestInfo())
            .then(response => response.json())
            .then(response => {
                return response.data;
            })
            .catch(error => {
                console.error('Error fetching news detail:', error);
                throw error;
            });
    }

    public saveReaction(postId: number, reactionId: number) {
        const url = `${Urls.URL_GIVE_LIKE}`; // URL para guardar la reacción
        console.log("Llamando a guardar like");
        const body = {
            news_id: postId,
            like_type_id: reactionId
        };

        // Modificamos la request info para usar el método POST y agregar el cuerpo con los datos necesarios
        const requestInfo = {
            ...this.getRequestInfo(),
            method: 'POST', // Aseguramos que el método sea POST
            headers: {
                'Content-Type': 'application/json', // Aseguramos que el servidor sepa que estamos enviando JSON
                ...this.getRequestInfo().headers, // Incluimos otros headers si es necesario
            },
            body: JSON.stringify(body), // Enviamos el cuerpo de la solicitud como JSON
        };

        return fetch(url, requestInfo)
            .then(response => response.json())
            .then(data => {
                console.log("data: " + JSON.stringify(data));
                return data;
            })
            .catch(error => {
                console.error('Error al guardar la reacción:', error);
                throw error;
            });
    }

    public removeReaction(newsId: number) {
        const url = `${Urls.URL_GIVE_LIKE}`; // La URL para eliminar la reacción
        console.log("Llamando a eliminar like");
    
        const body = {
            news_id: newsId
        };
    
        // Configuramos la solicitud con DELETE y el cuerpo en JSON
        const requestInfo = {
            ...this.getRequestInfo(),
            method: 'DELETE', // Cambiamos el método a DELETE
            headers: {
                'Content-Type': 'application/json', // Aseguramos que el servidor sepa que estamos enviando JSON
                ...this.getRequestInfo().headers
            },
            body: JSON.stringify(body) // Enviamos el cuerpo de la solicitud como JSON
        };
    
        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.message || 'Error al eliminar la reacción');
                    });
                }
                return response.json(); // Devuelve la respuesta en formato JSON
            })
            .then(data => {
                console.log("Reacción eliminada con éxito:", data);
                return data; // Retornamos los datos de la respuesta
            })
            .catch(error => {
                console.error('Error al eliminar la reacción:', error);
                throw error; // Re-lanzamos el error para que se pueda manejar más arriba
            });
    }
    

    public removePost(postId: number) {
        const url = `${Urls.URL_GET_NEWS}/${postId}`;

        const requestInfo = {
            ...this.getRequestInfo(),
            method: 'DELETE',  // Usamos el método DELETE
        };

        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        // Si la API devuelve un error en formato JSON, lanzamos ese mensaje
                        throw new Error(data.message || 'Error al eliminar el post');
                    });
                }
                return response.json(); // Devuelve la respuesta en formato JSON, si es necesario
            })
            .then(data => {
                console.log('Post eliminado con éxito:', data);
                return data;  // Devuelve los datos de la respuesta
            })
            .catch(error => {
                console.error('Error al eliminar el post:', error);
                throw error;  // Re-lanzamos el error para que se pueda manejar más arriba
            });
    }

    public async createNews(postContent: { content: string; isForAllCenters: string; publishingDate: string | null, mediaType: string | null; media?: File[], shareOptions: ShareCenterOption[] }) {
        const url = Urls.URL_GET_NEWS;
        const formData = new FormData();

        // Agrega el contenido
        formData.append('content', postContent.content);
        formData.append('shareOptions', JSON.stringify(postContent.shareOptions));
        formData.append('is_for_all_centers', postContent.isForAllCenters);

        if (postContent.publishingDate) {
            formData.append('publishingDate', postContent.publishingDate);
        }
    
        // Asegurarnos de que siempre enviamos 'media_type'
        const mediaType = postContent.mediaType || '';
        formData.append('media_type', mediaType);
    
        // Agrega los archivos de media, si existen
        if (postContent.media && postContent.mediaType) {
            if (postContent.mediaType === 'images') {
                // Si el tipo de medio es 'images', agregamos los archivos de imágenes
                postContent.media.forEach((file, index) => {
                    formData.append(`media[${index}]`, file);
                });
            }
        } else {
          formData.append('media[]', new Blob([]));
        }
    // Configura el método y la autorización
        const requestInfo = {
            ...this.getRequestInfo(),
            method: 'POST',
            body: formData,
            headers: {
                ...this.getRequestInfo().headers,
            }
        };
    
        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.message || 'Error al crear la noticia');
                    });
                }
                return response.json();
            })
            .then(async ({ data }) => {
                if (postContent.media && postContent.mediaType === 'video') {
                    const { id } = data;
                    await this.saveNewsChunked(id, postContent);
                }
                console.log('Noticia creada con éxito:', data);
                return data;
            })
            .catch(error => {
                console.error('Error al crear la noticia:', error);
                throw error;
            });
    }

    private saveNewsChunked(newsId: Post['id'], postContent: { media?: File[] }): Promise<void> {
        return this.sendWithVideoChunked(`${Urls.URL_GET_NEWS}/${newsId}/chunk`, postContent);
    }

    public async editNews(id: number, postContent: { content: string; isForAllCenters: string; publishingDate: string | null, mediaType: string | null; media?: File[]; shareOptions: ShareCenterOption[] }) {
        const url = `${Urls.URL_GET_NEWS}/${id}`;
        const formData = new FormData();

        // Agregar el contenido
        formData.append('content', postContent.content);

        // Convertir isForAllCenters a cadena 'true' o 'false'
        formData.append('is_for_all_centers', postContent.isForAllCenters ? 'true' : 'false');

        if (postContent.publishingDate) {
            formData.append('publishingDate', postContent.publishingDate);
        }

        // Si mediaType no es nulo, agregamos el tipo de medio
        if (postContent.mediaType) {
            formData.append('media_type', postContent.mediaType);
        }

        formData.append('shareOptions', JSON.stringify(postContent.shareOptions));

        // Agregar los archivos de media, si existen
        if (postContent.media && postContent.mediaType) {
            if (postContent.mediaType === 'images') {
                // Si el tipo de medio es 'images', agregamos los archivos de imágenes
                postContent.media.forEach((file, index) => {
                    formData.append(`media[${index}]`, file);
                });
            }
        }

        // Configurar el método y la autorización
        const requestInfo = {
            ...this.getRequestInfo(),
            method: 'POST',
            body: formData,
            headers: {
                ...this.getRequestInfo().headers, // No agregues 'Content-Type', fetch lo maneja automáticamente
            }
        };

        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.message || 'Error al editar la noticia');
                    });
                }
                return response.json();
            })
            .then(async ({ data }) => {
                if (postContent.media && postContent.mediaType === 'video') {
                    const { id } = data;
                    await this.saveNewsChunked(id, postContent);
                }
                console.log('Noticia editada con éxito:', data);
                return data;
            })
            .catch(error => {
                console.error('Error al editar la noticia:', error);
                throw error;
            });
    }

    public removeMedia(mediaId: number, mediaType: string, newsId: number): Promise<any> {
        const url = `${Urls.URL_GET_NEWS}/${newsId}/media`;

        console.log("mediaId: " + mediaId);
        console.log("url: " + url);

        const requestInfo = {
            ...this.getRequestInfo(),
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json', // Especificamos que el cuerpo será JSON
                ...this.getRequestInfo().headers, // Incluimos otros headers si es necesario
            },
            body: JSON.stringify({
                media_id: mediaId,
                media_type: mediaType,
                news_id: newsId
            }) // Enviamos el cuerpo de la solicitud como JSON
        };

        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        // Si la API devuelve un error en formato JSON, lanzamos ese mensaje
                        throw new Error(data.message || 'Error al eliminar el medio');
                    });
                }
                return response.json(); // Devuelve la respuesta en formato JSON, si es necesario
            })
            .then(data => {
                console.log('Medio eliminado con éxito:', data);
                return data;  // Devuelve los datos de la respuesta
            })
            .catch(error => {
                console.error('Error al eliminar el medio:', error);
                throw error;  // Re-lanzamos el error para que se pueda manejar más arriba
            });
    }

    public updateUserColor(color: string, fullName: string, email: string, phone: string | null) {
        const url = `${Urls.URL_GET_USER}`;

        const requestInfo = {
            ...this.getRequestInfo(),
            method: 'PUT',
            body: JSON.stringify({
                fullName: fullName,
                email: email,
                phone: phone,
                profileColor: color
            })
        };

        console.log("dataUpdateColor: " + JSON.stringify(requestInfo))

        return fetch(url, requestInfo)
            .then(response => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.message || 'Error al añadir el color de perfil del usuario');
                    });
                }
                return response.json();
            })
            .then(data => {
                return data;
            })
            .catch(error => {
                console.error('Error al añadir el color de perfil del usuario:', error);
                throw error;
            });
    }

    public getNewsShareOptions(newsId: Post['id']): Promise<ShareCenterOption[]> {
        const url = `${Urls.URL_GET_NEWS}/${newsId}/share-options`;

        return fetch(url, this.getRequestInfo())
            .then(response => response.json())
            .then((response: CustomResponse<ShareCenterOption[]>) => {
                if (!response.success) {
                    throw new Error(response.error?.[0])
                }
            
                return response.data!
            })
            .catch(error => {
                console.error('Error fetching news share options:', error);
                throw error;
            });
    }
}